import i18n from 'src/i18n';
import axios from 'axios';
import { LocalStorage } from 'src/utils/storage/local-storage';
import { CommonHelper } from 'src/utils/commonHelper';

const origin = window.location.origin;
const base = origin.includes('localhost') ? process.env.REACT_APP_API_URL : `${origin}/api`;

export function wholeMonthlyAmount(value) {
  if (!/^\d+$/.test(String(value).trim())) throw new Error(i18n.t('billing_payout.whole_amount'));
  const amount = Number(value);
  if (!Number.isSafeInteger(amount) || amount <= 0 || amount > 999999)
    throw new Error(i18n.t('billing_payout.amount_range'));
  return amount;
}

export const formatMonthlyAmount = (amount, currency) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(amount || 0);

async function request(path, method = 'GET', data, brandId, publicRequest = false) {
  try {
    if (!publicRequest) {
      const access = CommonHelper.GetBrandBillingAccess(brandId);
      const payoutRequest =
        path === 'reseller-payouts' || path.startsWith('reseller-payouts?') || path.startsWith('reseller-payouts/');
      const settingsRequest = path.endsWith('/billing');
      if (payoutRequest && !access.canManage) throw new Error(i18n.t('billing_payout.payout_admin_required'));
      if (!payoutRequest && !(settingsRequest && access.canManage) && !access.canPay)
        throw new Error(i18n.t('billing_payout.brand_admin_required'));
      if (settingsRequest && method === 'POST' && !access.canManage)
        throw new Error(i18n.t('billing_payout.org_admin_required'));
    }
    const response = await axios({
      url: publicRequest
        ? `${base}/integration-services-public/brand-billing/${path}`
        : `${base}/payment-services/${path}`,
      method,
      data,
      timeout: 120000,
      headers: publicRequest
        ? {}
        : {
            Authorization: LocalStorage.getItem('token') || '',
            BrandId: brandId || CommonHelper.GetCurrentBrandId() || '',
          },
    });
    if (response.data.http_code !== 200)
      throw new Error(response.data.message || i18n.t('billing_payout.request_failed'));
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || i18n.t('billing_payout.unavailable'));
  }
}

export const brandPayment = {
  payouts: (month) => request(`reseller-payouts?month=${encodeURIComponent(month)}`),
  savePayoutProfile: (paypal_email) => request('reseller-payouts/profile', 'POST', { paypal_email }),
  claimPayout: (data) => request('reseller-payouts/claims', 'POST', data),
  status: (id) => request(`brands/${id}/billing`, 'GET', undefined, id),
  save: (id, data) => request(`brands/${id}/billing`, 'POST', data, id),
  link: (id) => request(`brands/${id}/payment-link`, 'POST', {}, id),
  portal: (id) => request(`brands/${id}/portal`, 'POST', {}, id),
  refresh: (id) => request(`brands/${id}/refresh`, 'POST', {}, id),
  cancel: (id) => request(`brands/${id}/cancel-subscription`, 'POST', {}, id),
  keep: (id) => request(`brands/${id}/keep-subscription`, 'POST', {}, id),
  invoices: (id) => request(`brands/${id}/invoices`, 'GET', undefined, id),
  offer: (token) => request(`checkout/${token}`, 'GET', undefined, undefined, true),
  start: (token, id) => request(`brands/${id}/checkout/${token}`, 'POST', {}, id),
};

export const formatMoney = (amount, currency) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'INR',
  }).format((amount || 0) / 100);

export function redirectBillingResponse(response) {
  const payload = response?.data;
  if (response?.status === 402 || payload?.http_code === 402 || payload?.message === 'BRAND_PAYMENT_REQUIRED') {
    const id = payload?.data?.brand_id || CommonHelper.GetCurrentBrandId();
    if (id && !window.location.pathname.startsWith('/brand-payment/')) window.location.assign(`/brand-payment/${id}`);
  }
}
